import { mapToId, mapToKey } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { FILE_TYPE_ID } from '@witmetrics/utils/constants';
import type {
  DecoratedFileCollection,
  DecoratedFileCollectionFile,
  DecoratedScanServiceFileCollection,
  UndecoratedFile,
  UndecoratedScanService,
} from '@witmetrics/utils';
import { fetchFileCollectionData } from '@/utils/files';
import { API } from '@/api';

const { UPPER_ARCH, LOWER_ARCH, RETAINER, SPLINT } = FILE_TYPE_ID;

export const classes = {
  overlay:
    'w-[94vw] max-w-[1600px] px-6 py-4 gap-4 flex flex-col h-full overflow-hidden',
  content: 'flex w-full gap-4 grow overflow-hidden',
  leftColumn: 'h-full shrink-0 w-[350px] flex flex-col',
  loading:
    'h-full place-center text-xl text-grey-6 dark:text-mauve-3 font-normal',
  divider: 'mt-4 mb-0',
  viewerCard: 'grow p-0',
  viewer: 'size-full',
};

export async function fetchServiceSettings(serviceId: number) {
  return API.ScanServices.fetchScanServiceSettings(serviceId);
}

export async function fetchServiceData(serviceId: number): Promise<{
  service: UndecoratedScanService;
  serviceFileCollections: DecoratedScanServiceFileCollection[];
  fileCollections: DecoratedFileCollection[];
  fileCollectionFiles: DecoratedFileCollectionFile[];
  inputFiles: UndecoratedFile[];
  outputFiles: UndecoratedFile[];
}> {
  const [service, serviceFileCollections] = await Promise.all([
    API.ScanServices.fetchScanService(serviceId),
    API.ScanServices.fetchScanServiceFileCollections(serviceId),
  ]);
  if (isEmpty(serviceFileCollections)) {
    return {
      service,
      serviceFileCollections: [],
      fileCollections: [],
      fileCollectionFiles: [],
      inputFiles: [],
      outputFiles: [],
    };
  }
  // NOTE: There should only be one top-level file collection linked to the scanService
  const contents = await fetchFileCollectionData([
    serviceFileCollections[0].fileCollectionId,
  ]);
  return {
    service,
    serviceFileCollections,
    fileCollections: contents.fileCollections,
    fileCollectionFiles: contents.files,
    inputFiles: getCollectionFiles(
      'Input Files',
      contents.fileCollections,
      contents.files
    ),
    outputFiles: getCollectionFiles(
      'Output Files',
      contents.fileCollections,
      contents.files
    ),
  };
}

function getCollectionFiles(
  collectionName: string,
  fileCollections: DecoratedFileCollection[],
  fileCollectionFiles: DecoratedFileCollectionFile[]
) {
  const collection = fileCollections.find((fc) => fc.name === collectionName);
  if (!collection) return [];
  const files = fileCollectionFiles.filter(
    (fcf) => fcf.fileCollectionId === collection.id
  );
  return mapToKey(files, 'file');
}

export function getDefaultOpenIds(
  outputFiles: UndecoratedFile[],
  inputFiles: UndecoratedFile[]
): number[] {
  // If output files haven't been added to the service yet, just open all the input files so you don't get a blank screen
  if (isEmpty(outputFiles)) return mapToId(inputFiles);
  return mapToId(
    outputFiles.filter(
      (f) =>
        f.fileTypeId &&
        [UPPER_ARCH, LOWER_ARCH, RETAINER, SPLINT].includes(f.fileTypeId)
    )
  );
}
